import React, { useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import { Product } from '../types';

export interface ProductSectionCarouselProps {
  title: string;
  subtitle?: string;
  products: Product[];
  trackRef?: (el: HTMLDivElement | null) => void;
  sectionClassName?: string;
  headerExtra?: React.ReactNode;
}

const ProductSectionCarousel: React.FC<ProductSectionCarouselProps> = ({
  title,
  subtitle,
  products,
  trackRef,
  sectionClassName = 'py-8 md:py-12 bg-white',
  headerExtra,
}) => {
  const internalRef = useRef<HTMLDivElement | null>(null);

  const setTrackRef = useCallback(
    (el: HTMLDivElement | null) => {
      internalRef.current = el;
      trackRef?.(el);
    },
    [trackRef]
  );

  const scroll = (direction: 'left' | 'right') => {
    const track = internalRef.current;
    if (!track) return;
    const slide = track.querySelector<HTMLElement>('.product-carousel-slide');
    const gap = 16;
    const amount = slide ? slide.offsetWidth + gap : Math.min(track.clientWidth * 0.85, 320);
    track.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  if (!products.length) return null;

  return (
    <section className={sectionClassName}>
      <div className="container mx-auto px-4 min-w-0 max-w-full">
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-gray-600 text-sm mt-1 line-clamp-2">{subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerExtra}
            <button
              type="button"
              onClick={() => scroll('left')}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation"
              aria-label="Scroll products left"
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            </button>
            <button
              type="button"
              onClick={() => scroll('right')}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors touch-manipulation"
              aria-label="Scroll products right"
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>
        </div>

        <div className="product-carousel-viewport -mx-4 px-4 sm:mx-0 sm:px-0">
          <div
            ref={setTrackRef}
            className="product-carousel-track"
            role="list"
            aria-label={`${title} products`}
          >
            {products.map((product) => (
              <div
                key={product._id}
                className="product-carousel-slide"
                role="listitem"
              >
                <ProductCard product={product} variant="carousel" className="w-full h-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductSectionCarousel;
